import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AlignContent } from 'components/data-containers/TerritoryTable/shared/TerritoryTable.style';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper/index';
import CountryFlag from 'components/base/CountryFlag/index';
import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import Table from 'components/data-visualization/Table/Table';

import metrics from 'maps/metrics';

import useMedia from 'hooks/useMedia';
import useTerritoryGridData from './utils/useTerritoryGridData';
import getShortName from 'helpers/getShortName';

import {
  TableHeader,
  TableRow,
  HorizontalScrollContainer,
} from 'components/Overview/BreakdownTable/styles';

import {
  BoldEntityCell,
  HighlightedEntityCell,
} from 'components/data-visualization/Table/DoubleMetricCell.style';
import Layout from 'components/styled/Layout/Layout';
import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';
import { RootState } from 'redux/rootReducer';
import { EntityTypes } from 'context/entityContext';
import { IValueRanges } from 'components/data-containers/types';
import { HeaderWrapper } from 'components/data-visualization/Table/DropdownHead';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { MAX_COUNTRY_NAME } from 'config';

interface ITerritoryData {
  entityData: {
    entity: {
      id: string;
      type: EntityTypes;
    };
    totalCountryCount: number;
    countries: {
      countryId: string;
      countryName: string;
      data: {
        countryRank: number;
        llw: number;
        lw: number;
        lwtd: number;
        rtd: number;
        rtdOfGlobal: number;
        salesType: string;
        twtd: number;
        twtdOfGlobal: number;
        wowInProgress: number;
        wowLastCompleted: number;
      }[];
    }[];
  };
  valueRanges: IValueRanges;
}

interface ITerritoryTable {
  title: string;
  activeMetric: string;
  isPending: boolean;
  headers: string[];
  territoryData: ITerritoryData[];
  highlightedCountryId?: string;
}

const TerritoryTable: React.FC<ITerritoryTable> = (props) => {
  const {
    headers,
    territoryData,
    isPending,
    activeMetric,
    highlightedCountryId = '',
    title,
  } = props;

  const countries = useSelector((state: RootState) => state.countries.data);
  const rows = useTerritoryGridData(
    territoryData,
    countries,
    highlightedCountryId
  );

  const isLargeScreen = useMedia('(min-width: 800px)');

  /**
   * True if there are no breakdowns to display.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (Array.isArray(rows)) {
      return rows.length === 0;
    }
    return !isPending;
  }, [rows, isPending]);

  return (
    <HorizontalScrollContainer>
      <AnimatedLoadingWrapper loading={isPending}>
        <Layout.Relative>
          <h4>{title}</h4>
          <Table>
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <TableHeader
                    key={header}
                    isRightAligned={index === 2}
                    minWidth={index === 2 ? '11em' : ''}
                  >
                    <HeaderWrapper
                      condition={header
                        .toLowerCase()
                        .includes('this week to date')}
                    >
                      {header}
                    </HeaderWrapper>
                  </TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {isPending && <TableLoader columnCount={headers.length} />}
              {hasNoData && !isPending && (
                <BreakdownTablePlaceholder
                  isSmallScreen={!isLargeScreen}
                  columnCount={headers.length}
                />
              )}
              {rows &&
                rows.map((row) => {
                  const name = isLargeScreen ? row.displayName : row.id;
                  const countryName = getShortName(name);
                  return (
                    <TableRow
                      key={`territory-breakdown-row-${row.id}`}
                      isRightAligned
                    >
                      <BoldEntityCell>#{row.rank}</BoldEntityCell>
                      <HighlightedEntityCell highlighted={row.highlighted}>
                        <TooltipWrapper
                          condition={name.length > MAX_COUNTRY_NAME}
                          tooltipContent={name}
                        >
                          <AlignContent alignLeft>
                            <CountryFlag
                              id={row.id}
                              displayName={row.displayName}
                              flagImgSmall={row.flagImgSmall}
                              flagImgMedium={row.flagImgMedium}
                              flagImgLarge={row.flagImgLarge}
                              small
                            />
                            <span>{countryName}</span>
                          </AlignContent>
                        </TooltipWrapper>
                      </HighlightedEntityCell>

                      <DoubleMetricCell
                        entityData={row.values}
                        header={metrics[activeMetric]}
                        sortBy={metrics[activeMetric]}
                      />
                    </TableRow>
                  );
                })}
            </tbody>
          </Table>
        </Layout.Relative>
      </AnimatedLoadingWrapper>
    </HorizontalScrollContainer>
  );
};

export default TerritoryTable;
